import React from 'react';
import {
  ComboBox,
  ComboBoxBlurEvent,
  ComboBoxChangeEvent,
  ComboBoxFilterChangeEvent,
  ComboBoxFocusEvent,
  ComboBoxPageChangeEvent
} from '@progress/kendo-react-dropdowns';
import _ from 'lodash';
import { Label } from '@progress/kendo-react-labels';
import classNames from 'classnames';
import Field from '../layout/field/Field';
import { FIELD_OMITTED_PROPS } from '../../utility';
import {
  ComboBoxCloseEvent,
  ComboBoxOpenEvent,
  ComboBoxProps
} from '@progress/kendo-react-dropdowns/dist/npm/ComboBox/ComboBoxProps';
import { COMBO_WITH_VALUE_PROPS } from './Types';

const isPresent = (value: string) => value !== null && value !== undefined;

function withValueField(DropDownComponent: React.ForwardRefExoticComponent<ComboBoxProps & React.RefAttributes<any>>) {
  return class extends React.Component<COMBO_WITH_VALUE_PROPS> {
    component: any;
    translatedData: any;
    inputId;

    static defaultProps = {
      size: 'medium'
    }

    /**
     * Adding aria-required attribute to input - accessibility
     */
    componentDidMount() {
      const { required, id } = this.props;
      if (required) this.component._input.setAttribute('aria-required', 'true');
      const input = this.component._input;
      this.inputId = id ? id : input.id;
    }

    events = {
      onBlur: (event: ComboBoxBlurEvent) => this.triggerEvent('onBlur', event),
      onFocus: (event: ComboBoxFocusEvent) => this.triggerEvent('onFocus', event),
      onChange: (event: ComboBoxChangeEvent) => {
        const { props = {}, state = {} } = this.component;
        const opened = props.opened !== undefined ? props.opened : state.opened;
        if (opened && (props.handleFormChange || props.handleChangeByState) && (event.nativeEvent instanceof KeyboardEvent)) {
          if (event.nativeEvent.altKey || (event.nativeEvent.keyCode !== 40 && event.nativeEvent.keyCode !== 38)) {
            this.triggerEvent('onChange', event);
          } else if (props.handleFormChange) {
            const e = {
              ...event,
              target: {
                value: event.target.value[props.valueField],
                name: event.target.name
              }
            };
            props.handleFormChange(e);
          }
        } else {
          this.triggerEvent('onChange', event);
        }
        if (!event.value) {
          event.target?.focus();
        }
      },
      onClose: (event: ComboBoxCloseEvent) => {
        const { props = {} } = this.component;
        if ((props.handleFormChange || props.handleChangeByState) && !(event.nativeEvent instanceof MouseEvent)) {
          this.triggerEvent('onChange', event);
        }
        this.triggerEvent('onClose', event);
      },
      onOpen: (event: ComboBoxOpenEvent) => this.triggerEvent('onOpen', event),
      onPageChange: (event: ComboBoxPageChangeEvent) => this.triggerEvent('onPageChange', event),
      onFilterChange: (event: ComboBoxFilterChangeEvent) => this.triggerEvent('onFilterChange', event)
    };

    get value() {
      let returnValue;
      if (this.component) {
        const value = this.component.value;
        returnValue = isPresent(value) ? value[this.props.valueField] : value;
      }

      return returnValue;
    }

    render() {
      const {
        asField,
        ariaDescribedBy,
        data,
        defaultValue,
        errorTxt,
        disabled,
        translate,
        label,
        labelClassName,
        valid,
        value,
        size,
        withValidation,
        fieldClassName,
        fieldMandatory,
        fieldWithAdditionalEl,
        dataCy
      } = this.props;

      const comboProps = { ...this.props };
      const filteredComboProps = _.omit(comboProps, ['label', ...FIELD_OMITTED_PROPS]);
      this.translatedData = translate ? data.map(this.translateItem.bind(this)) : data;
      const popupSettingsClass = this.props?.popupSettings?.className;
      const fieldWrapCls = classNames(
        'tp-100',
        {
          'k-medium': size === 'medium',
          'k-state-empty': !value,
          'k-state-invalid': valid === false,
          'k-state-disabled': disabled
        }
      );

      const asFieldProps = {
        fieldClassName,
        fieldMandatory,
        fieldWithAdditionalEl
      };

      const comboRender = (
        <div
          className={fieldWrapCls}
          data-cy={dataCy ? `${dataCy}-wrapper` : undefined}
        >
          {label && <Label
            editorId={this.inputId} 
            className={classNames({
              [labelClassName]: labelClassName,
              'k-mandatory': !asField && fieldMandatory
            })}
          >
            {label}
          </Label>}
          <DropDownComponent
            id={this.inputId}
            data-cy={dataCy ? dataCy : undefined}
            {...filteredComboProps}
            value={this.itemFromValue(value)}
            defaultValue={this.itemFromValue(defaultValue)}
            data={this.translatedData}
            popupSettings={{
              className: popupSettingsClass ? popupSettingsClass + ' tp-single-select-menu' : 'tp-single-select-menu'
            }}
            ref={(component: any) => this.component = component}
            {...this.events}
          />
          {withValidation && <div
            id={ariaDescribedBy}
            data-cy={dataCy ? `${dataCy}-error-message` : undefined}
            className={'tp-validation-error-message'}
          >
            {errorTxt}
          </div>}
        </div>
      );

      if (asField) {
        return (
          <Field {...asFieldProps}>
            {comboRender}
          </Field>
        );
      }

      return comboRender;
    }

    translateItem(item: any[]) {
      const { textField, translate, name } = this.props;
      return {
        ...item,
        [textField]: translate(name + '.' + item[textField])
      };
    }

    /**
     * Helper method for triggering events outside the cmp itself
     * */
    triggerEvent(eventType: string, event: ComboBoxChangeEvent | ComboBoxPageChangeEvent | ComboBoxBlurEvent) {
      if (this.props[eventType]) {
        this.props[eventType].call(undefined, {
          ...event,
          target: this
        });
      }
    }

    itemFromValue<T extends {}>(selectedOption: T): T {
      const { data = [], valueField = '' } = this.props;
      const selectedValue = selectedOption?.[valueField] || selectedOption;
      return isPresent(selectedValue) && valueField ?
        data.find((item: T) => item[valueField] === selectedValue) : selectedOption;
    }
  };
}

const ComboWithValue = withValueField(ComboBox);
export default ComboWithValue;
